// src/components/menus/shared/MenuElements.tsx
import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDragScroll } from '../../../hooks/useDragScroll';

interface SubMenuContainerProps {
  bottomBorder?: boolean;
}

interface SubMenuCapProps {
  height?: string;
}

const SubMenuContainerRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bottomBorder',
})<SubMenuContainerProps>(({ theme, bottomBorder = true }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0',
  padding: '0',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.tertiary.main,
  borderBottom: bottomBorder
    ? theme.palette.mode === 'dark'
      ? `2px solid ${theme.palette.grey[900]}`
      : `2px solid ${theme.palette.secondary.dark}`
    : 'none',
  whiteSpace: 'nowrap',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  paddingRight: '1rem',
  '&::-webkit-scrollbar': { display: 'none' },
}));

export const SubMenuContainer: React.FC<
  React.ComponentProps<typeof SubMenuContainerRoot>
> = (props) => {
  const dragRef = useDragScroll<HTMLDivElement>();
  return <SubMenuContainerRoot ref={dragRef} {...props} />;
};

export const SubMenuCap = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'height',
})<SubMenuCapProps>(({ theme, height = '4.23rem' }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0',
  minWidth: '1rem',
  height,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.tertiary.dark,
  borderRight: `2px solid ${theme.palette.primary.dark}`,
  borderBottom: `2px solid ${theme.palette.primary.dark}`,
}));

const CompactSubMenuContainerRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  padding: 0,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.tertiary.main,
  borderBottom:
    theme.palette.mode === 'dark'
      ? `2px solid ${theme.palette.grey[900]}`
      : `2px solid ${theme.palette.secondary.dark}`,
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

export const CompactSubMenuContainer: React.FC<
  React.ComponentProps<typeof CompactSubMenuContainerRoot>
> = (props) => {
  const dragRef = useDragScroll<HTMLDivElement>();
  return <CompactSubMenuContainerRoot ref={dragRef} {...props} />;
};

export const CompactSubMenuTab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'minWidth',
})<{ isActive?: boolean; minWidth?: string }>(
  ({ theme, isActive, minWidth }) => ({
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.3rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: isActive ? '3px' : 'normal',
    whiteSpace: 'nowrap',
    padding: '0.5rem 1.25rem',
    minHeight: '38px',
    minWidth: minWidth || '100px',
    borderLeft: isActive ? 'none' : '2px solid',
    borderColor: theme.palette.primary.dark,
    transition:
      'letter-spacing 0.3s ease, background-color 0.2s ease, color 0.2s ease',
    backgroundColor: isActive
      ? theme.palette.secondary.main
      : theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.light,
    color: isActive
      ? '#ffffff'
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[50]
        : theme.palette.tertiary.dark,
    '&:hover': {
      backgroundColor: isActive
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
      color: '#ffffff',
      letterSpacing: '3px',
      borderColor: theme.palette.primary.dark,
    },
  })
);
